import { useState } from "react";
import { useKV } from "@/hooks/use-kv-adapter";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Goal } from "@/types/performance";
import { Target, Plus, CheckCircle, Warning, Clock } from "@phosphor-icons/react";
import { toast } from "sonner";

export function GoalTracker() {
  const [goals, setGoals] = useKV<Goal[]>("performance-goals", []);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [newGoal, setNewGoal] = useState<Partial<Goal>>({
    title: "",
    type: "Individual",
    status: "Not Started",
    progress: 0,
    keyResults: []
  });

  const handleCreateGoal = () => {
    if (!newGoal.title) {
      toast.error("Title is required");
      return;
    }
    const goal: Goal = {
      id: crypto.randomUUID(),
      title: newGoal.title || "",
      description: newGoal.description || "",
      type: newGoal.type as any,
      status: "Not Started",
      progress: 0,
      dueDate: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString(), // +90 days
      ownerId: "CURRENT_USER",
      ownerName: "Me",
      keyResults: []
    };
    setGoals([...goals, goal]);
    setIsDialogOpen(false);
    setNewGoal({ title: "", type: "Individual", status: "Not Started", progress: 0 });
    toast.success("Goal created successfully");
  };

  const updateProgress = (id: string, newProgress: number) => {
    const updated = goals.map(g => g.id === id ? { ...g, progress: newProgress, status: newProgress === 100 ? 'Completed' : newProgress > 0 ? 'In Progress' : 'Not Started' } as Goal : g);
    setGoals(updated);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">My Goals & OKRs</h3>
        <Button onClick={() => setIsDialogOpen(true)}>
          <Plus className="mr-2 h-4 w-4" /> New Goal
        </Button>
      </div>

      <div className="grid gap-4">
        {goals.map((goal) => (
          <Card key={goal.id}>
            <CardContent className="pt-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">{goal.type}</Badge>
                    <h4 className="font-semibold text-lg">{goal.title}</h4>
                  </div>
                  {goal.description && <p className="text-sm text-slate-500 mt-1">{goal.description}</p>}
                </div>
                <Badge className={
                  goal.status === 'Completed' ? 'bg-green-100 text-green-700' :
                  goal.status === 'At Risk' ? 'bg-red-100 text-red-700' :
                  'bg-blue-100 text-blue-700'
                }>
                  {goal.status}
                </Badge>
              </div>

              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Progress</span>
                  <span>{goal.progress}%</span>
                </div>
                <Progress value={goal.progress} className="h-2" />
              </div>

              <div className="flex gap-4 mt-6">
                <Button variant="outline" size="sm" onClick={() => updateProgress(goal.id, Math.min(100, goal.progress + 10))}>
                  <CheckCircle className="mr-2 h-4 w-4" /> Update Progress
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
        
        {goals.length === 0 && (
          <div className="text-center py-12 text-slate-500 border-2 border-dashed rounded-lg">
            <Target className="w-12 h-12 mx-auto mb-2 opacity-20" />
            <p>No goals set for this quarter.</p>
            <Button variant="link" onClick={() => setIsDialogOpen(true)}>Create one now</Button>
          </div>
        )}
      </div>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Set New Goal</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Goal Title</Label>
              <Input 
                placeholder="e.g. Increase sales by 20%" 
                value={newGoal.title}
                onChange={(e) => setNewGoal({...newGoal, title: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label>Type</Label>
              <Select 
                value={newGoal.type} 
                onValueChange={(v) => setNewGoal({...newGoal, type: v as any})}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Individual">Individual</SelectItem>
                  <SelectItem value="Department">Department</SelectItem>
                  <SelectItem value="Company">Company</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Input 
                placeholder="Details..." 
                value={newGoal.description}
                onChange={(e) => setNewGoal({...newGoal, description: e.target.value})}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateGoal}>Create Goal</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
